import React, { useContext, useEffect, useReducer, useState } from "react";
import { Link as RouterLink, useHistory, useLocation } from "react-router-dom";

import ListSubheader from "@material-ui/core/ListSubheader";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ListItem from "@material-ui/core/ListItem";
import Divider from "@material-ui/core/Divider";
import {
  Badge,
  makeStyles,
  Tooltip,
  useTheme,
} from "@material-ui/core";

import WhatsAppIcon from "../assets/icon/WhatsAppIcon";
import { i18n } from "../translate/i18n";

import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { useChat } from '../context/Chat/ChatContext';

import { Can } from "../components/Can";

import { getSocket } from "../services/socket";
import api from "../services/api";

import toastError from "../errors/toastError";

import SubscriptionIcon from "../assets/icon/SubscriptionIcon";
import ConnectionsIcon from "../assets/icon/ConnectionsIcon";
import DepartmentIcon from "../assets/icon/DepartmentIcon";
import DashBoardIcon from "../assets/icon/DashBoardIcon";
import TaskBoardIcon from "../assets/icon/TaskBoardIcon";
import CalendarIcon from "../assets/icon/CalendarIcon";
import ContactIcon from "../assets/icon/ContactIcon";
import UsersIcon from "../assets/icon/UsersIcon";
import ChatIcon from "../assets/icon/ChatIcon";
import TagsIcon from "../assets/icon/TagsIcon";
import FastIcon from "../assets/icon/FastIcon";
import DocumentTextIcon from "../assets/icon/DocumentTextIcon";

const useStyles = (drawerOpen) => {
  return makeStyles((theme) => ({
    root: {
      background: theme.palette.sidebarBackground,
      color: theme.palette.sideBarIcon,
      overflowY: "scroll",
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": {
        display: "none",
      },
      "-ms-overflow-style": "none",
      height: "100vh",
      padding: "20px 0",
      margin: drawerOpen ? "0px 18px 0 18px" : "0px 10px 0 10px",
    },
    listItem: {
      paddingRight: "3px",
      marginTop: 2,
      borderRadius: "15px",
      display: "flex",
      alignItems: "center",
      transition: "background-color , border-radius",
      "&:hover": {
        backgroundColor: "rgba(255, 245, 218, 0.24)",
        color: theme.palette.primary.main,
        "& svg path": {
          stroke: theme.palette.primary.main,
          transition: "stroke ",
        },
      },
    },
    selected: {
      marginLeft: 1,
      backgroundColor: "rgba(255, 245, 218, 0.24)",
      borderRadius: "15px",
      color: theme.palette.primary.main,
      transition: "background-color , border-radius",
      "& svg path": {
        stroke: theme.palette.primary.main,
        transition: "stroke ",
      },
    },
    listItemText: {
      fontWeight: theme.typography.fontWeightMedium,
    },
    subheader: {
      color: theme.palette.text.secondary,
      margin: "10px 0 0 20px",
    },
    collapse: {
      paddingLeft: drawerOpen ? theme.spacing(3) : theme.spacing(1),
    },
  }))();
};

function ListItemLink(props) {
  const classes = useStyles();
  const { icon, primary, to, className } = props;

  const renderLink = React.useMemo(
    () =>
      React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to]
  );

  return (
    <ListItem button component={renderLink} className={className}>
      {icon ? (
        <Tooltip title={primary} arrow placement="right">
          <ListItemIcon className={classes.navBarIcon}>{icon}</ListItemIcon>
        </Tooltip>
      ) : null}
      <ListItemText primary={primary} />
    </ListItem>
  );
}

const reducer = (state, action) => {
  if (action.type === "LOAD_CHATS") {
    if (!action.payload || !Array.isArray(action.payload)) return state;
    
    return action.payload.map(chat => {
      if (!chat) return null;
      return {
        ...chat,
        users: Array.isArray(chat.users) ? chat.users : []
      };
    }).filter(Boolean);
  }

  if (action.type === "UPDATE_CHATS") {
    if (!action.payload) return state;
    
    const chat = {
      ...action.payload,
      users: Array.isArray(action.payload.users) ? action.payload.users : []
    };
    
    const chatIndex = state.findIndex((u) => u.id === chat.id);
    if (chatIndex !== -1) {
      const newState = [...state];
      newState[chatIndex] = chat;
      return newState;
    }
    return [chat, ...state];
  }

  if (action.type === "CHANGE_CHAT") {
    if (!action.payload?.chat) return state;
    
    return state.map((chat) => {
      if (chat.id === action.payload.chat.id) {
        return {
          ...action.payload.chat,
          users: Array.isArray(action.payload.chat.users) 
            ? action.payload.chat.users 
            : []
        };
      }
      return chat;
    });
  }

  if (action.type === "DELETE_CHAT") {
    const chatId = action.payload;
    return state.filter(chat => chat.id !== chatId);
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const MainListItems = (props) => {
  const classes = useStyles();
  const { drawerClose, drawerOpen } = props;
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);
  const [connectionWarning, setConnectionWarning] = useState(false);
  const [connectionBadged, setConnectionBadged] = useState(false);
  const [openCampaignSubmenu, setOpenCampaignSubmenu] = useState(false);
  const [showCampaigns, setShowCampaigns] = useState(false);
  const history = useHistory();
  const theme = useTheme();

  const [invisible, setInvisible] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam] = useState("");
  const [chats, dispatch] = useReducer(reducer, []);

  const location = useLocation();
  const { unreadCount } = useChat();

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchChats();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const socket = getSocket(user?.companyId);

    if (socket) {
      const handleEventChat = (data) => {
        try {
          if (!data || !data.action) return;

          if (data.action === "update" && data.chat) {
            dispatch({
              type: "UPDATE_CHATS",
              payload: data.chat
            });
          } else if (data.action === "delete" && data.chatId) {
            dispatch({
              type: "DELETE_CHAT",
              payload: data.chatId
            });
          }
        } catch (err) {
          console.error("Error procesando evento de socket:", err);
        }
      }
      socket.on(`company-${user?.companyId}-chat`, handleEventChat);

      return () => {
        socket.off(`company-${user?.companyId}-chat`, handleEventChat);
      };
    }
  }, [user?.companyId]);

  useEffect(() => {
    let unreadsCount = 0;
    if (chats.length > 0) {
      for (let chat of chats) {
        for (let chatUser of chat.users) {
          if (chatUser.userId === user.id) {
            unreadsCount += chatUser.unreads;
          }
        }
      }
    }
    if (unreadsCount > 0) {
      setInvisible(false);
    } else {
      setInvisible(true);
    }
  }, [chats, user.id]);

  useEffect(() => {
    if (localStorage.getItem("cshow")) {
      setShowCampaigns(true);
    }
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        
        const hasActiveConnections = whatsApps.some(whats => whats.status === "CONNECTED");
        
        if (offlineWhats.length === whatsApps.length || !hasActiveConnections) {
          setConnectionWarning(true);
          setConnectionBadged(true);
        } else {
          setConnectionWarning(false);
          setConnectionBadged(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  const fetchChats = async () => {
    try {
      const { data } = await api.get("/chats/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_CHATS", payload: data.records });
    } catch (err) {
      toastError(err);
    }
  };

  const commonItems = (
    <>
      <Can
        role={user.profile}
        perform="dashboard:view"
        yes={() => (
          <ListItemLink
            to="/"
            primary={i18n.t("mainDrawer.listItems.dashboard")}
            icon={<DashBoardIcon fill={theme.palette.sideBarIcon} />}
            className={`${classes.listItem} ${
              location.pathname === "/" ? classes.selected : ""
            }`}
          />
        )}
      />

      <ListItemLink
        to="/tickets"
        primary={i18n.t("mainDrawer.listItems.tickets")}
        icon={<WhatsAppIcon fill={theme.palette.sideBarIcon} />}
        className={`${classes.listItem} ${
          location.pathname === "/tickets" ? classes.selected : ""
        }`}
      />

      <ListItemLink
        to="/contacts"
        primary={i18n.t("mainDrawer.listItems.contacts")}
        icon={<ContactIcon fill={theme.palette.sideBarIcon} />}
        className={`${classes.listItem} ${
          location.pathname === "/contacts" ? classes.selected : ""
        }`}
      />

      <ListItemLink
        to="/schedules"
        primary={i18n.t("mainDrawer.listItems.schedules")}
        icon={<CalendarIcon fill={theme.palette.sideBarIcon} />}
        className={`${classes.listItem} ${
          location.pathname === "/schedules" ? classes.selected : ""
        }`}
      />

      <ListItemLink
        to="/quick-messages"
        primary={i18n.t("mainDrawer.listItems.quickMessages")}
        icon={<FastIcon fill={theme.palette.sideBarIcon} />}
        className={`${classes.listItem} ${
          location.pathname === "/quick-messages" ? classes.selected : ""
        }`}
      />
      <ListItemLink
        to="/whatsapp-templates"
        primary={i18n.t("mainDrawer.listItems.whatsappTemplates")}
        icon={<DocumentTextIcon fill={theme.palette.sideBarIcon} />}
        className={`${classes.listItem} ${
          location.pathname === "/whatsapp-templates" ? classes.selected : ""
        }`}
      />
      <ListItemLink
        to="/tags"
        primary={i18n.t("mainDrawer.listItems.tags")}
        icon={<TagsIcon fill={theme.palette.sideBarIcon} />}
        className={`${classes.listItem} ${
          location.pathname === "/tags" ? classes.selected : ""
        }`}
      />

      <ListItemLink
        to="/connections"
        primary={i18n.t("mainDrawer.listItems.connections")}
        icon={
          <Badge badgeContent={connectionBadged ? "!" : 0} color="error">
            <ConnectionsIcon fill={theme.palette.sideBarIcon} />
          </Badge>
        }
        className={`${classes.listItem} ${
          location.pathname === "/connections" ? classes.selected : ""
        }`}
      />
    </>
  );

  return (
    <div onClick={drawerClose} className={classes.root}>
      {commonItems}
        <ListItemLink
            to='/kanban'
            primary={i18n.t("mainDrawer.listItems.kanban")}
            icon={<TaskBoardIcon fill={theme.palette.sideBarIcon} /> }
            className={`${classes.listItem} ${location.pathname === '/kanban' ? classes.selected : ''}`}
        />

        <ListItemLink
            to="/chats"
            primary={i18n.t("mainDrawer.listItems.chats")}
            icon={
                <Badge color="secondary" variant="dot" invisible={unreadCount === 0}>
                    <ChatIcon fill={theme.palette.sideBarIcon} />
                </Badge>
            }
            className={`${classes.listItem} ${location.pathname === '/chats' ? classes.selected : ''}`}
        />

      {/* Comentado: Tareas
        <ListItemLink
            to="/todolist"
            primary={i18n.t("mainDrawer.listItems.todolist")}
            icon={<TaskIcon fill={theme.palette.sideBarIcon} />}
            className={`${classes.listItem} ${location.pathname === '/todolist' ? classes.selected : ''}`}
        />
        */}
      {/*  {showCampaigns && (
            <>
                <ListItem
                    button
                    onClick={() => setOpenCampaignSubmenu((prev) => !prev)}
                    className={`${classes.listItem} ${['/campaigns', '/contact-lists', '/campaigns-config'].includes(location.pathname) ? classes.selected : ''}`}
                >
                    <ListItemIcon>
                        <CampaignsIcon fill={theme.palette.sideBarIcon}  />
                    </ListItemIcon>
                    <ListItemText
                        primary={i18n.t("mainDrawer.listItems.campaigns")}
                    />
                    {openCampaignSubmenu ? (
                        <ExpandLessIcon />
                    ) : (
                        <ExpandMoreIcon />
                    )}
                </ListItem>
                <Collapse
                    className={classes.collapse}
                    in={openCampaignSubmenu}
                    timeout="auto"
                    unmountOnExit
                >
                    <List component="div" disablePadding>
                        <ListItem onClick={() => history.push("/campaigns")} button 
                        selected={location.pathname === '/campaigns'} 
                        className={`${classes.listItem} ${location.pathname === '/campaigns' ? classes.selected : ''}`} 
                        >
                            <ListItemIcon>
                            <CalendarIcon fill={theme.palette.sideBarIcon} />
                            </ListItemIcon>
                            <ListItemText primary="Campañas" />
                        </ListItem>
                        <ListItem
                            onClick={() => history.push("/contact-lists")}
                            button
                            selected={location.pathname === '/contact-lists'}
                            className={`${classes.listItem} ${location.pathname === '/contact-lists' ? classes.selected : ''}`} 
                        >
                            <ListItemIcon>
                                <UsersIcon fill={theme.palette.sideBarIcon}  />
                            </ListItemIcon>
                            <ListItemText primary="Listas de contactos" />
                        </ListItem>
                        <ListItem
                            onClick={() => history.push("/campaigns-config")}
                            button
                            selected={location.pathname === '/campaigns-config'}
                            className={`${classes.listItem} ${location.pathname === '/campaigns-config' ? classes.selected : ''}`} 
                        >
                            <ListItemIcon>
                                <SettingsIcon fill={theme.palette.sideBarIcon} />
                            </ListItemIcon>
                            <ListItemText primary="Ajustes - deprecable" />
                        </ListItem>
                    </List>
                </Collapse>
            </>
        )}
        */}

      {/* {user.profile === "admin" && (
            <ListItemLink
                to="/announcements"
                primary={i18n.t("mainDrawer.listItems.annoucements")}
                icon={<TrayIconSide fill={theme.palette.sideBarIcon}  />}
                className={`${classes.listItem} ${location.pathname === '/announcements' ? classes.selected : ''}`} 
            />
        )} */}

      {/* Comentado: Ayuda
        <ListItemLink
            to="/helps"
            primary={i18n.t("mainDrawer.listItems.helps")}
            icon={<HelpIcon fill={theme.palette.sideBarIcon} />}
            className={`${classes.listItem} ${location.pathname === '/helps' ? classes.selected : ''}`} 
        />
        */}
      {user.profile === "admin" && (
        <>
          <Divider />
          <ListSubheader inset style={{ color: theme.palette.sideBarIcon }}>
            {i18n.t("mainDrawer.listItems.administration")}
          </ListSubheader>

          <ListItemLink
            to="/Departaments"
            primary={i18n.t("mainDrawer.listItems.queues")}
            icon={<DepartmentIcon fill={theme.palette.sideBarIcon} />}
            className={`${classes.listItem} ${
              location.pathname === "/queues" ? classes.selected : ""
            }`}
          />

          <ListItemLink
            to="/users"
            primary={i18n.t("mainDrawer.listItems.users")}
            icon={<UsersIcon fill={theme.palette.sideBarIcon} />}
            className={`${classes.listItem} ${
              location.pathname === "/users" ? classes.selected : ""
            }`}
          />
          {/* 
            <ListItemLink
              to="/messages-api"
              primary={i18n.t("mainDrawer.listItems.messagesAPI")}
              icon={<TerminalIcon fill={theme.palette.sideBarIcon} />}
              className={`${classes.listItem} ${
                location.pathname === "/messages-api" ? classes.selected : ""
              }`}
            /> */}

          <ListItemLink
              to="/subscription"
              primary={i18n.t("mainDrawer.listItems.financeiro")}
              icon={<SubscriptionIcon fill={theme.palette.sideBarIcon} />}
              className={`${classes.listItem} ${
                location.pathname === "/subscription" ? classes.selected : ""
              }`}
            />

          {/* <ListItemLink
              to="/settings"
              primary={i18n.t("mainDrawer.listItems.settings")}
              icon={<SettingsIcon fill={theme.palette.sideBarIcon} />}
              className={`${classes.listItem} ${
                location.pathname === "/settings" ? classes.selected : ""
              }`}
            /> */}
        </>
      )}
    </div>
  );
};

export default MainListItems;
